import { FieldValues, FieldPath, Control, RegisterOptions, Controller } from 'react-hook-form';
import { AppTagInputField, AppTagInputFieldProps } from '../fields/tagInput/tagInput';

type HookFormTagInputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<AppTagInputFieldProps, 'values' | 'onDeleteTag' | 'onAddTag'> & {
  name: TName;
  control: Control<TFieldValues>;
  rules?: Exclude<RegisterOptions, 'valueAsDate' | 'setValueAs'>;
  onFocus?: () => void;
  defaultValue?: unknown;
  canAddTagValidationCallback?: (newTag: string) => boolean;
};

export function HookFormTagInputField(props: HookFormTagInputFieldProps) {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ field: { value, onChange, ref }, fieldState: { error }, formState: { isSubmitting } }) => (
        <AppTagInputField
          ref={ref}
          values={value}
          onDeleteTag={(index: number) => {
            if (index < 0) return;

            const nextValue = value?.filter((_: string, currentIndex: number) => currentIndex !== index) ?? [];
            onChange(nextValue.length ? nextValue : null);
          }}
          onAddTag={(newTag: string) => {
            if (value?.includes(newTag)) return;

            // Custom validation for new tag
            const result = props.canAddTagValidationCallback ? props.canAddTagValidationCallback(newTag) : true;
            if (!result) throw new Error('Invalid pattern');

            onChange([...(value || []), newTag]);
          }}
          {...props}
          errorProps={{
            ...props.errorProps,
            errors: error?.message ?? props.errorProps?.errors,
          }}
          disabled={props.disabled || isSubmitting}
        />
      )}
    />
  );
}
